// src/pages/RFQPortal.tsx — Sprint 2 G04: award quote → auto-generate draft PO ref
import { useState } from 'react'
import { useAuthStore } from '../stores/auth.store'
import { MOCK_RFQS, RFQ_STATUS_META, RFQ, SupplierQuoteResponse, RFQStatus, getDaysUntilClosing, getLowestQuote, getSavingVsHighest } from '../utils/mockDataRFQ'
import { Send, Plus, Search, ChevronRight, X, CheckCircle, AlertTriangle, Clock, Building, Calendar, Download, Lock, ArrowRight, FileText, DollarSign, TrendingDown } from 'lucide-react'
import { addDraftPO } from '../utils/workflowEvents'

const fmtAUD  = (n: number) => new Intl.NumberFormat('en-AU', { style:'currency', currency:'AUD', minimumFractionDigits:2 }).format(n)
const fmtDate = (s?: string) => s ? new Date(s).toLocaleDateString('en-AU', { day:'numeric', month:'short', year:'2-digit' }) : '—'

// ── G04: build a draft PO reference from winning quote ────────────
function buildDraftPOFromQuote(rfq: RFQ, quote: SupplierQuoteResponse): { poRef: string; supplier: string; totalExGST: number } {
  const jobNum = rfq.jobRef.replace('TP-','').replace('-','')
  const poRef = `PO-${jobNum}-DRAFT-${Date.now().toString().slice(-4)}`
  return { poRef, supplier: quote.supplierName, totalExGST: quote.subtotalExGST }
}

// ── Create RFQ Wizard ─────────────────────────────────────────────
function CreateRFQWizard({ onClose }: { onClose: () => void }) {
  const [step, setStep]       = useState(1)
  const [title, setTitle]     = useState('')
  const [closingDate, setCD]  = useState('')
  const [lines, setLines]     = useState([{ description:'', qty:1, unit:'each', notes:'' }])
  const [suppliers, setSupp]  = useState<string[]>([])
  const [submitted, setSub]   = useState(false)
  const supplierOpts = ['Prysmian Group (Olex)','Saferoads Queensland','Aldridge Traffic Architects','Boral Construction Materials','Blackwoods Australia','Nexans Australia','Iplex Pipelines']
  if (submitted) return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-8 text-center">
        <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4"><Send className="w-8 h-8 text-[#2E75B6]"/></div>
        <h3 className="text-lg font-bold text-gray-900 mb-2">RFQ sent</h3>
        <p className="text-sm text-gray-600">RFQ-2026-005 sent to {suppliers.length} supplier{suppliers.length!==1?'s':''}.</p>
        <button onClick={onClose} className="mt-6 btn-primary w-full">Done</button>
      </div>
    </div>
  )
  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-start justify-center overflow-y-auto py-8 px-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl">
        <div className="flex items-center justify-between px-6 py-4 border-b bg-gray-50 rounded-t-2xl">
          <div>
            <h3 className="font-bold text-gray-900">Create new RFQ</h3>
            <div className="flex items-center gap-2 mt-1.5">
              {[1,2,3,4].map(s=>(
                <span key={s} className="flex items-center gap-1.5">
                  <span className={`w-6 h-6 rounded-full text-xs font-bold flex items-center justify-center ${step>s?'bg-green-500 text-white':step===s?'bg-[#1F3864] text-white':'bg-gray-200 text-gray-400'}`}>{step>s?'✓':s}</span>
                  <span className={`text-xs hidden sm:block ${step===s?'text-[#1F3864] font-medium':'text-gray-400'}`}>{['Details','Scope','Suppliers','Review'][s-1]}</span>
                  {s<4&&<span className="text-gray-200 hidden sm:block">›</span>}
                </span>
              ))}
            </div>
          </div>
          <button onClick={onClose}><X className="w-4 h-4 text-gray-400"/></button>
        </div>
        <div className="p-6 space-y-4">
          {step===1&&(<div className="grid grid-cols-2 gap-3">
            <div className="col-span-2"><label className="block text-xs font-medium text-gray-700 mb-1">RFQ title <span className="text-red-500">*</span></label><input className="input text-sm" value={title} onChange={e=>setTitle(e.target.value)} placeholder="e.g. 3C+E cable supply — TP-2026-003"/></div>
            <div><label className="block text-xs font-medium text-gray-700 mb-1">Job</label><select className="input text-sm"><option>TP-2026-001</option><option>TP-2026-003</option></select></div>
            <div><label className="block text-xs font-medium text-gray-700 mb-1">Closing date <span className="text-red-500">*</span></label><input type="date" className="input text-sm" value={closingDate} onChange={e=>setCD(e.target.value)}/></div>
          </div>)}
          {step===2&&(<div className="space-y-2">
            {lines.map((l,i)=>(
              <div key={i} className="border border-gray-200 rounded-xl p-3 space-y-2">
                <div className="flex justify-between"><span className="text-xs font-semibold text-gray-600">Line {i+1}</span>{lines.length>1&&<button onClick={()=>setLines(p=>p.filter((_,j)=>j!==i))}><X className="w-3.5 h-3.5 text-red-400"/></button>}</div>
                <div className="grid grid-cols-4 gap-2">
                  <div className="col-span-4"><input className="input text-xs" value={l.description} onChange={e=>setLines(p=>p.map((x,j)=>j===i?{...x,description:e.target.value}:x))} placeholder="e.g. UPVC 100mm conduit"/></div>
                  <div><input type="number" className="input text-xs" value={l.qty} onChange={e=>setLines(p=>p.map((x,j)=>j===i?{...x,qty:parseInt(e.target.value)||1}:x))}/></div>
                  <div><select className="input text-xs" value={l.unit} onChange={e=>setLines(p=>p.map((x,j)=>j===i?{...x,unit:e.target.value}:x))}>{['each','m','m²','kg'].map(u=><option key={u}>{u}</option>)}</select></div>
                </div>
              </div>
            ))}
            <button onClick={()=>setLines(p=>[...p,{description:'',qty:1,unit:'each',notes:''}])} className="w-full btn-secondary text-xs"><Plus className="w-3.5 h-3.5 inline mr-1"/>Add line</button>
          </div>)}
          {step===3&&(<div className="space-y-2">{supplierOpts.map(s=>(
            <label key={s} className={`flex items-center gap-3 p-3 rounded-xl border cursor-pointer ${suppliers.includes(s)?'border-[#2E75B6] bg-blue-50':'border-gray-200 hover:bg-gray-50'}`}>
              <input type="checkbox" className="accent-[#2E75B6]" checked={suppliers.includes(s)} onChange={e=>setSupp(p=>e.target.checked?[...p,s]:p.filter(x=>x!==s))}/>
              <span className="text-sm font-medium text-gray-900">{s}</span>
            </label>
          ))}</div>)}
          {step===4&&(<div className="bg-[#1F3864] rounded-xl p-4 text-white"><div className="font-bold text-lg mb-1">{title||'Untitled RFQ'}</div><div className="text-xs text-blue-300">Closes {fmtDate(closingDate)} · {lines.length} line{lines.length!==1?'s':''} · {suppliers.length} supplier{suppliers.length!==1?'s':''}</div></div>)}
          <div className="flex justify-between pt-2">
            <button onClick={()=>step>1?setStep(s=>s-1):onClose()} className="btn-secondary text-sm">{step===1?'Cancel':'← Back'}</button>
            {step<4?<button onClick={()=>setStep(s=>s+1)} disabled={step===1&&!title} className="btn-primary text-sm disabled:opacity-50">Next →</button>
              :<button onClick={()=>setSub(true)} className="btn-primary text-sm flex items-center gap-2"><Send className="w-4 h-4"/>Send RFQ</button>}
          </div>
        </div>
      </div>
    </div>
  )
}

// ── Quote Comparison Panel ────────────────────────────────────────
function QuoteComparisonPanel({ rfq, canAward, onClose }: { rfq: RFQ; canAward: boolean; onClose: ()=>void }) {
  const [awardConfirm, setAwardConfirm] = useState<string|null>(null)
  const [createdPO, setCreatedPO]       = useState<{poRef:string;supplier:string;totalExGST:number}|null>(null)
  const sorted = [...rfq.responses].sort((a,b)=>a.subtotalExGST-b.subtotalExGST)
  const savings = getSavingVsHighest(rfq)

  const handleAward = (quoteId: string) => {
    const q = rfq.responses.find(r=>r.id===quoteId)
    if (!q) return
    const po = buildDraftPOFromQuote(rfq, q)
    setCreatedPO(po)
    setAwardConfirm(null)
    // P3-2: persist draft PO to shared store so PurchaseOrders can surface it for review
    addDraftPO({ poRef: po.poRef, rfqRef: rfq.reference, jobRef: rfq.jobRef, supplier: po.supplier, totalExGST: po.totalExGST, createdAt: new Date().toISOString() })
  }

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-start justify-center overflow-y-auto py-8 px-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-5xl">
        <div className="flex items-center justify-between px-6 py-4 border-b bg-gray-50 rounded-t-2xl">
          <div><h3 className="font-bold text-gray-900">Quote comparison — {rfq.reference}</h3><p className="text-xs text-gray-500 mt-0.5">{rfq.title}</p></div>
          {savings>0&&<div className="flex items-center gap-1.5 bg-green-50 border border-green-200 rounded-xl px-3 py-1.5 text-xs text-green-700 font-semibold"><TrendingDown className="w-3.5 h-3.5"/>Potential saving: {fmtAUD(savings)}</div>}
          <button onClick={onClose}><X className="w-4 h-4 text-gray-400"/></button>
        </div>

        {/* G04 — PO created banner */}
        {createdPO&&(
          <div className="mx-6 mt-4 bg-green-50 border border-green-200 rounded-xl px-4 py-3 flex items-center gap-3">
            <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0"/>
            <div>
              <div className="text-sm font-semibold text-green-900">Draft PO created — <span className="font-mono">{createdPO.poRef}</span></div>
              <div className="text-xs text-green-700">Awarded to {createdPO.supplier} · {fmtAUD(createdPO.totalExGST)} ex GST · Go to Purchase Orders to review and approve.</div>
            </div>
          </div>
        )}

        <div className="overflow-x-auto">
          <table className="w-full text-xs min-w-max">
            <thead>
              <tr className="border-b bg-gray-50">
                <th className="text-left px-5 py-3 font-medium text-gray-600 w-64">Line item</th>
                {sorted.map((q,i)=>(
                  <th key={q.id} className={`px-4 py-3 text-center font-medium min-w-40 ${i===0?'bg-green-50 text-green-800':'text-gray-600'}`}>
                    <div className="font-semibold">{q.supplierName}</div>
                    {i===0&&<div className="text-[10px] bg-green-200 text-green-800 rounded-full px-2 py-0.5 mt-1 font-bold">★ Lowest price</div>}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rfq.lineItems.map(line=>(
                <tr key={line.id} className="border-b border-gray-50">
                  <td className="px-5 py-3"><div className="font-medium text-gray-900">{line.description}</div><div className="text-[10px] text-gray-400">{line.qty} {line.unit}</div></td>
                  {sorted.map((q,i)=>{
                    const ql=q.lines.find(l=>l.rfqLineId===line.id)
                    const lowest=Math.min(...sorted.map(s=>s.lines.find(l=>l.rfqLineId===line.id)?.unitPriceExGST??Infinity))
                    return (
                      <td key={q.id} className={`px-4 py-3 text-center ${i===0?'bg-green-50/60':''}`}>
                        {ql?.available?<div><div className={`font-bold tabular-nums ${ql.unitPriceExGST===lowest?'text-green-700':'text-gray-900'}`}>{fmtAUD(ql.unitPriceExGST)}/{line.unit}</div><div className="text-gray-400 tabular-nums">{fmtAUD(ql.totalExGST)}</div></div>:<span className="text-gray-300">—</span>}
                      </td>
                    )
                  })}
                </tr>
              ))}
              <tr className="border-t-2 border-gray-200 bg-gray-50 font-semibold">
                <td className="px-5 py-3 text-sm text-gray-900">Total (ex GST)</td>
                {sorted.map((q,i)=>(
                  <td key={q.id} className={`px-4 py-3 text-center ${i===0?'bg-green-50':''}`}>
                    <div className={`text-sm font-bold tabular-nums ${i===0?'text-green-700':'text-gray-900'}`}>{fmtAUD(q.subtotalExGST)}</div>
                    <div className="text-xs text-gray-400 tabular-nums">{fmtAUD(q.totalIncGST)} inc GST</div>
                  </td>
                ))}
              </tr>
              <tr className="bg-gray-50">
                <td className="px-5 py-3 text-xs font-semibold text-gray-700">Award &amp; create PO</td>
                {sorted.map((q,i)=>(
                  <td key={q.id} className={`px-4 py-3 text-center ${i===0?'bg-green-50/60':''}`}>
                    {canAward?(
                      awardConfirm===q.id?(
                        <div className="space-y-1.5">
                          <div className="text-xs text-gray-700 font-medium">Award to {q.supplierName}?</div>
                          <div className="flex gap-1.5 justify-center">
                            <button onClick={()=>setAwardConfirm(null)} className="btn-secondary text-xs py-0.5 px-2">Cancel</button>
                            <button onClick={()=>handleAward(q.id)} className="btn-primary text-xs py-0.5 px-2 flex items-center gap-1"><FileText className="w-3 h-3"/>Award &amp; create PO</button>
                          </div>
                        </div>
                      ):(
                        <button onClick={()=>setAwardConfirm(q.id)} className={`text-xs px-3 py-1.5 rounded-lg font-semibold transition-colors ${i===0?'bg-green-500 text-white hover:bg-green-600':'bg-gray-200 text-gray-700 hover:bg-gray-300'}`}>
                          {i===0?'★ Award (lowest)':'Award'}
                        </button>
                      )
                    ):(
                      <span className="text-xs text-gray-400 flex items-center justify-center gap-1"><Lock className="w-3 h-3"/>PM only</span>
                    )}
                  </td>
                ))}
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )
}

// ── RFQ Detail Panel ──────────────────────────────────────────────
function RFQDetailPanel({ rfq, onClose, isPM, isPC, onUpdateRFQ }: { rfq: RFQ; onClose:()=>void; isPM:boolean; isPC:boolean; onUpdateRFQ?: (c: Partial<RFQ>)=>void }) {
  const [showCompare, setShowCompare] = useState(false)
  const sm = RFQ_STATUS_META[rfq.status]
  const daysLeft = getDaysUntilClosing(rfq)
  const lowestQuote = getLowestQuote(rfq)
  return (
    <div className="flex flex-col h-full">
      <div className="px-5 py-4 border-b bg-gray-50 flex-shrink-0">
        <div className="flex items-start justify-between gap-3">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap mb-1">
              <span className="text-xs font-mono text-gray-400">{rfq.reference}</span>
              <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${sm.bg} ${sm.text}`}>{sm.icon} {sm.label}</span>
            </div>
            <div className="font-semibold text-gray-900 text-sm">{rfq.title}</div>
            <div className="text-xs text-gray-500 mt-0.5 flex gap-3 flex-wrap">
              <span>📁 {rfq.jobRef}</span>
              <span>📅 Closes {fmtDate(rfq.closingDate)}{rfq.status==='SENT'&&<> ({daysLeft}d)</>}</span>
            </div>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X className="w-4 h-4"/></button>
        </div>
      </div>
      <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
        <div className="grid grid-cols-2 gap-3 text-sm">
          <div><div className="text-xs text-gray-500 mb-0.5">Invited suppliers</div><div className="font-medium">{rfq.invitedSupplierNames.length}</div></div>
          <div><div className="text-xs text-gray-500 mb-0.5">Responses received</div><div className="font-medium">{rfq.responses.length}</div></div>
          {lowestQuote&&<div className="col-span-2"><div className="text-xs text-gray-500 mb-0.5">Lowest quote</div><div className="font-bold text-green-700">{fmtAUD(lowestQuote.subtotalExGST)} ex GST — {lowestQuote.supplierName}</div></div>}
        </div>
        {rfq.responses.length>0&&(
          <div className="space-y-2">
            <div className="text-xs font-semibold text-gray-700 uppercase tracking-wide">Supplier responses</div>
            {[...rfq.responses].sort((a,b)=>a.subtotalExGST-b.subtotalExGST).map((r,i)=>(
              <div key={r.id} className={`rounded-xl border px-4 py-3 ${i===0?'bg-green-50 border-green-200':'bg-white border-gray-200'}`}>
                <div className="flex items-center justify-between">
                  <div><div className="font-semibold text-sm text-gray-900">{r.supplierName}</div><div className="text-xs text-gray-500">{r.deliveryLeadDays}d lead · {r.paymentTermsDays}d terms</div></div>
                  <div className="text-right"><div className={`font-bold tabular-nums ${i===0?'text-green-700':'text-gray-900'}`}>{fmtAUD(r.subtotalExGST)}</div><div className="text-xs text-gray-400">ex GST</div></div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      <div className="border-t px-5 py-3 flex gap-2 flex-shrink-0">
        {rfq.status==='RESPONSES_RECEIVED'&&(
          <button onClick={()=>setShowCompare(true)} className="btn-primary text-sm flex items-center gap-2"><ArrowRight className="w-4 h-4"/>Compare &amp; award</button>
        )}
        <button className="btn-secondary text-sm flex items-center gap-2"><Download className="w-3.5 h-3.5"/>Export</button>
      </div>
      {showCompare&&<QuoteComparisonPanel rfq={rfq} canAward={isPM} onClose={()=>setShowCompare(false)}/>}
    </div>
  )
}

// ── Main page ─────────────────────────────────────────────────────
export function RFQPortal() {
  const { user, isTier } = useAuthStore()
  const isPC    = isTier('B_PC'); const isPM = isTier('B_PM'); const isAdmin = isTier('A_DEVELOPER')
  const [rfqList, setRFQList] = useState(MOCK_RFQS)
  const [selected, setSelected] = useState<RFQ|null>(null)
  const [statusFilter, setStatus] = useState<RFQStatus|'ALL'>('ALL')
  const [search, setSearch] = useState('')
  const [showCreate, setShowCreate] = useState(false)
  const updateRFQ = (id: string, changes: Partial<RFQ>) => setRFQList(p=>p.map(r=>r.id===id?{...r,...changes}:r))
  const filtered = rfqList.filter(r=>(statusFilter==='ALL'||r.status===statusFilter)&&(!search||r.title.toLowerCase().includes(search.toLowerCase())||r.reference.toLowerCase().includes(search.toLowerCase())))
  const awaitingAction = rfqList.filter(r=>r.status==='RESPONSES_RECEIVED').length
  const closingSoon    = rfqList.filter(r=>r.status==='SENT'&&getDaysUntilClosing(r)<=3).length

  return (
    <div className="space-y-5">
      <div className="flex items-start justify-between gap-4">
        <div><h1 className="text-xl font-bold text-gray-900 flex items-center gap-2"><Send className="w-5 h-5 text-[#2E75B6]"/>Supplier RFQ Portal</h1><p className="text-sm text-gray-500 mt-0.5">Create RFQ · compare quotes · award and auto-create PO</p></div>
        {isPC&&<button onClick={()=>setShowCreate(true)} className="btn-primary flex items-center gap-2 text-sm"><Plus className="w-4 h-4"/>Create RFQ</button>}
      </div>

      <div className="grid grid-cols-4 gap-3">
        {[
          { label:'Total RFQs',         value:rfqList.length,                                              color:'text-gray-900',  sub:'all time' },
          { label:'Awaiting comparison',value:awaitingAction, color:awaitingAction>0?'text-amber-600':'text-gray-400', sub:'responses received' },
          { label:'Closing soon',        value:closingSoon,    color:closingSoon>0?'text-red-600':'text-gray-400',     sub:'within 3 days' },
          { label:'Awarded this year',   value:rfqList.filter(r=>r.status==='AWARDED').length,             color:'text-green-700', sub:'supplier selected' },
        ].map(({label,value,color,sub})=>(
          <div key={label} className="bg-white border border-gray-200 rounded-xl px-4 py-3">
            <div className={`text-2xl font-bold ${color}`}>{value}</div>
            <div className="text-xs font-medium text-gray-700 mt-0.5">{label}</div>
            <div className="text-xs text-gray-400">{sub}</div>
          </div>
        ))}
      </div>

      {awaitingAction>0&&<div className="bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 flex items-center gap-3 text-sm text-amber-800"><AlertTriangle className="w-4 h-4 text-amber-600 flex-shrink-0"/><span><strong>{awaitingAction} RFQ{awaitingAction!==1?'s':''}</strong> ready for comparison and award.</span></div>}

      <div className="flex gap-2 flex-wrap items-center">
        <div className="relative flex-1 min-w-48"><Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400"/><input className="input pl-9 text-sm" placeholder="Search RFQs…" value={search} onChange={e=>setSearch(e.target.value)}/></div>
        {(['ALL','DRAFT','SENT','RESPONSES_RECEIVED','AWARDED'] as (RFQStatus|'ALL')[]).map(f=>{
          const count=f==='ALL'?rfqList.length:rfqList.filter(r=>r.status===f).length
          if(count===0&&f!=='ALL') return null
          return <button key={f} onClick={()=>setStatus(f)} className={`text-xs px-3 py-1.5 rounded-full font-medium transition-colors ${statusFilter===f?'bg-[#1F3864] text-white':'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>{f==='ALL'?'All':RFQ_STATUS_META[f as RFQStatus].label} <span className="opacity-60">{count}</span></button>
        })}
      </div>

      <div className="flex gap-4 items-start">
        <div className="flex-1 min-w-0 space-y-2">
          {filtered.map(rfq=>{
            const sm=RFQ_STATUS_META[rfq.status]; const daysLeft=getDaysUntilClosing(rfq); const isUrgent=rfq.status==='SENT'&&daysLeft<=3; const needsAction=rfq.status==='RESPONSES_RECEIVED'; const isSel=selected?.id===rfq.id
            return (
              <div key={rfq.id} onClick={()=>setSelected(isSel?null:rfq)}
                className={`bg-white border rounded-xl p-4 cursor-pointer transition-all hover:shadow-sm ${isSel?'border-blue-400 ring-2 ring-blue-100':needsAction?'border-amber-300':isUrgent?'border-red-200':'border-gray-200 hover:border-gray-300'}`}>
                <div className="flex items-start gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                      <span className="text-xs font-mono text-gray-400">{rfq.reference}</span>
                      <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${sm.bg} ${sm.text}`}>{sm.icon} {sm.label}</span>
                      {needsAction&&<span className="text-xs bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded-full font-semibold">Compare quotes →</span>}
                      {isUrgent&&<span className="text-xs bg-red-100 text-red-700 px-1.5 py-0.5 rounded-full font-semibold flex items-center gap-1"><Clock className="w-2.5 h-2.5"/>{daysLeft}d</span>}
                    </div>
                    <div className="font-semibold text-gray-900 text-sm">{rfq.title}</div>
                    <div className="flex items-center gap-3 mt-1.5 flex-wrap text-xs text-gray-500">
                      <span className="flex items-center gap-1"><Building className="w-3 h-3"/>{rfq.jobRef}</span>
                      <span className="flex items-center gap-1"><Calendar className="w-3 h-3"/>Closes {fmtDate(rfq.closingDate)}</span>
                      <span>{rfq.invitedSupplierNames.length} invited · {rfq.responses.length} responded</span>
                      {rfq.responses.length>0&&<span className="text-green-600 font-medium">Lowest: {fmtAUD(Math.min(...rfq.responses.map(r=>r.subtotalExGST)))}</span>}
                    </div>
                  </div>
                  <ChevronRight className={`w-4 h-4 flex-shrink-0 mt-1 ${isSel?'text-blue-500':'text-gray-400'}`}/>
                </div>
              </div>
            )
          })}
        </div>
        {selected&&(
          <div className="w-[480px] flex-shrink-0 bg-white border border-gray-200 rounded-xl overflow-hidden shadow-sm sticky top-6" style={{maxHeight:'calc(100vh - 160px)'}}>
            <RFQDetailPanel rfq={selected} onClose={()=>setSelected(null)} isPM={isPM||isAdmin} isPC={isPC} onUpdateRFQ={c=>updateRFQ(selected.id,c)}/>
          </div>
        )}
      </div>
      {showCreate&&<CreateRFQWizard onClose={()=>setShowCreate(false)}/>}
    </div>
  )
}
